/** Home page and the "Needs you" bell (ADR-0055). */
export default {
  en: {
    title: "Home",
    greeting: "Welcome, {{name}}",
    purpose: "What needs you now, where to start, and how the platform works.",
    needs: {
      title: "Needs you now",
      none: "Nothing needs you right now.",
      loading: "Checking what needs you…",
      error: "Could not check",
      bell: "Needs you",
      bellCount: "Needs you: {{count}}",
      bellTitle: "Waiting for you",
      inbox: { title: "Tasks waiting for your decision", action: "Open the inbox" },
      desk: { title: "Conversations waiting for a person", action: "Open the desk" },
      cases: { title: "Cases assigned to you", action: "Open cases" },
      promotions: { title: "Releases waiting for your sign-off", action: "Review releases" },
      budgets: { title: "Spending limits near or over the limit", action: "View spending limits" },
      certifications: { title: "Marketplace listings to certify", action: "Review listings" },
      globalKill: { title: "Global stop requests to confirm", action: "Open the emergency stop" },
      notifications: { title: "New notifications from agents", action: "Open notifications" },
    },
    start: {
      title: "Start here",
      ask: { title: "Ask an agent", body: "Type a question in plain words. The assistant answers, or asks a person to approve first." },
      inbox: { title: "Decide waiting tasks", body: "Approve, edit or reject what agents want to do. The most urgent come first." },
      cases: { title: "Work on a case", body: "See everything about one piece of work: people, documents, tasks and history." },
      channels: { title: "Answer customers", body: "Take over conversations that agents handed to a person." },
      builder: { title: "Build or change an agent", body: "Describe the steps, test with sample questions, then submit for approval." },
      knowledge: { title: "Add knowledge", body: "Upload documents so agents can answer with sources." },
      models: { title: "Choose AI models", body: "Decide which approved models agents may use, and what happens if one is down." },
      governance: { title: "Check what is live", body: "See which agents are live, sign off new releases and stop an agent quickly if something goes wrong." },
      privacy: { title: "Handle a privacy request", body: "Find and erase what agents remember about a person." },
      admin: { title: "Add people and roles", body: "Create user accounts and decide what each person may do." },
      operator: { title: "Watch operations", body: "See alerts, spending limits and capacity at a glance." },
      executive: { title: "See the outcomes", body: "How much work agents completed, and what it saved." },
      learn: { title: "Learn how it works", body: "A two-minute interactive picture of what happens to a request." },
    },
    how: {
      title: "How it works",
      lead: "Every request follows the same safe path.",
      ask: { label: "Ask", body: "You ask an agent in the assistant or a channel." },
      approve: { label: "Approve (if needed)", body: "Important actions wait for a person to approve." },
      answer: { label: "Answer", body: "The agent completes the work and replies." },
      recorded: "Every step is recorded in the <term>audit ledger</term>.",
      more: "See the full picture",
      tour: "Take the tour",
    },
  },
  ar: {
    title: "الرئيسية",
    greeting: "أهلًا بك، {{name}}",
    purpose: "ما يحتاج إليك الآن، ومن أين تبدأ، وكيف تعمل المنصة.",
    needs: {
      title: "يحتاج إليك الآن",
      none: "لا شيء يحتاج إليك الآن.",
      loading: "جارٍ التحقق مما يحتاج إليك…",
      error: "تعذّر التحقق",
      bell: "يحتاج إليك",
      bellCount: "يحتاج إليك: {{count}}",
      bellTitle: "بانتظارك",
      inbox: { title: "مهام تنتظر قرارك", action: "فتح صندوق المراجعة" },
      desk: { title: "محادثات تنتظر موظفًا", action: "افتح المكتب" },
      cases: { title: "حالات مسندة إليك", action: "فتح الحالات" },
      promotions: { title: "إصدارات بانتظار موافقتك", action: "مراجعة الإصدارات" },
      budgets: { title: "حدود إنفاق قاربت نهايتها أو تجاوزتها", action: "عرض حدود الإنفاق" },
      certifications: { title: "عناصر في السوق بانتظار الشهادة", action: "مراجعة العناصر" },
      globalKill: { title: "طلبات إيقاف شامل بانتظار التأكيد", action: "فتح الإيقاف الطارئ" },
      notifications: { title: "إشعارات جديدة من الوكلاء", action: "فتح الإشعارات" },
    },
    start: {
      title: "ابدأ من هنا",
      ask: { title: "اسأل وكيلًا", body: "اكتب سؤالك بكلمات بسيطة. يجيب المساعد، أو يطلب اعتماد شخص أولًا." },
      inbox: { title: "قرّر في المهام المنتظرة", body: "اعتمد ما يريد الوكلاء فعله أو عدّله أو ارفضه. الأكثر إلحاحًا يأتي أولًا." },
      cases: { title: "اعمل على حالة", body: "اطّلع على كل ما يخص عملًا واحدًا: الأشخاص والمستندات والمهام والسجل." },
      channels: { title: "أجب العملاء", body: "تولَّ المحادثات التي حوّلها الوكلاء إلى موظف." },
      builder: { title: "أنشئ وكيلًا أو عدّله", body: "صف الخطوات، واختبر بأسئلة نموذجية، ثم أرسله للاعتماد." },
      knowledge: { title: "أضف معرفة", body: "ارفع مستندات ليجيب الوكلاء مع ذكر المصادر." },
      models: { title: "اختر نماذج الذكاء الاصطناعي", body: "حدد النماذج المعتمدة التي يمكن للوكلاء استخدامها، وما يحدث إذا تعطل أحدها." },
      governance: { title: "تحقق مما هو قيد التشغيل", body: "اطّلع على الوكلاء العاملين، ووقّع على الإصدارات الجديدة، وأوقف أي وكيل بسرعة إن حدث خطأ." },
      privacy: { title: "عالج طلب خصوصية", body: "ابحث عما يتذكره الوكلاء عن شخص وامحه." },
      admin: { title: "أضف أشخاصًا وأدوارًا", body: "أنشئ حسابات المستخدمين وحدد ما يمكن لكل شخص فعله." },
      operator: { title: "راقب العمليات", body: "اطّلع على التنبيهات وحدود الإنفاق والسعة بنظرة واحدة." },
      executive: { title: "اطّلع على النتائج", body: "حجم العمل الذي أنجزه الوكلاء وما وفّره." },
      learn: { title: "تعرّف على طريقة العمل", body: "صورة تفاعلية في دقيقتين لما يحدث للطلب." },
    },
    how: {
      title: "كيف تعمل المنصة",
      lead: "كل طلب يسلك المسار الآمن نفسه.",
      ask: { label: "اسأل", body: "تسأل وكيلًا في المساعد أو عبر قناة." },
      approve: { label: "الاعتماد (عند الحاجة)", body: "الإجراءات المهمة تنتظر موافقة شخص." },
      answer: { label: "الإجابة", body: "يُنجز الوكيل العمل ويرد عليك." },
      recorded: "تُسجَّل كل خطوة في <term>سجل التدقيق</term>.",
      more: "اطّلع على الصورة الكاملة",
      tour: "ابدأ الجولة",
    },
  },
};
